import React, { type FC, useState } from 'react'
import { Box, FormControlLabel, Typography } from '@mui/material'

import { NotificationSeverity } from '../../../domain'

import { Banner, type BannerSeverity, Button, Checkbox, InlineAlert, type InlineAlertContent } from '../common'
import { useAppUsecase } from '../State'
import { SplitButtonControl } from './SplitButtonControl'

const severities = [
    NotificationSeverity.ERROR,
    NotificationSeverity.WARNING,
    NotificationSeverity.INFO,
    NotificationSeverity.SUCCESS,
] as const satisfies readonly NotificationSeverity[]

const bannerSeverities = [NotificationSeverity.ERROR, NotificationSeverity.WARNING, NotificationSeverity.INFO] as const satisfies readonly BannerSeverity[]

type InlineAlertContentKind = InlineAlertContent['kind']

const inlineAlertContentKinds = ['title', 'titleAndDescription', 'descriptionOnly', 'errorList'] as const satisfies readonly InlineAlertContentKind[]

const inlineAlertContentByKind: Readonly<Record<InlineAlertContentKind, InlineAlertContent>> = {
    title: { kind: 'title', title: 'Title only inline alert' },
    titleAndDescription: { kind: 'titleAndDescription', title: 'Title and description', description: 'Additional context for the alert.' },
    descriptionOnly: { kind: 'descriptionOnly', description: 'Description-only inline alert, no title.' },
    errorList: { kind: 'errorList', title: 'Multiple errors', errors: ['First error', 'Second error'] },
}

const TOAST_PLAYGROUND_DEDUPE_KEY = 'notifications-storybook-toast-playground'
const BANNER_PLAYGROUND_DISMISS_KEY = 'notifications-storybook-banner-playground'

const sectionTitleStyle = { display: 'block', mb: 4 } as const
const playgroundStyle = { display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-start', mb: 10 } as const
const playgroundControlsStyle = { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 10, minWidth: 240 } as const
const previewStyle = { display: 'flex', alignItems: 'flex-start', minHeight: 64 } as const
const fixedBannerPreviewStyle = { ...previewStyle, flexGrow: 1 } as const

const Toggle: FC<Readonly<{ testId: string, label: string, checked: boolean, onChange: (checked: boolean) => void }>> = ({
    testId,
    label,
    checked,
    onChange,
}) => <FormControlLabel label={label} control={<Checkbox data-testid={testId} checked={checked} onChange={(_, value) => onChange(value)} />} />

/** Triggers the real `toastUsecase`, rendered by the global `<ToastPortal/>` - not a local preview. */
const ToastPlayground: FC = () => {
    const toastUsecase = useAppUsecase('toastUsecase')

    const [severity, setSeverity] = useState<NotificationSeverity>(NotificationSeverity.INFO)
    const [showDescription, setShowDescription] = useState(true)
    const [showAction, setShowAction] = useState(false)
    const [aggregate, setAggregate] = useState(false)
    const [persist, setPersist] = useState(false)

    const handleShow = (): void => {
        toastUsecase.show({
            severity,
            title: 'Toast title',
            persist,
            ...(aggregate && { dedupeKey: TOAST_PLAYGROUND_DEDUPE_KEY }),
            ...(showDescription && { description: 'Optional description text' }),
            ...(showAction && { action: { label: 'Undo', onClick: () => {} } }),
        })
    }

    return (
        <Box sx={playgroundControlsStyle}>
            <SplitButtonControl
                ariaLabel="Toast severity"
                testIdPrefix="notifications-storybook-toast-severity"
                option={severity}
                options={severities}
                onChange={setSeverity}
            />
            <Toggle
                testId="notifications-storybook-toast-toggle-description"
                label="Show description"
                checked={showDescription}
                onChange={setShowDescription}
            />
            <Toggle testId="notifications-storybook-toast-toggle-action" label="Show action" checked={showAction} onChange={setShowAction} />
            <Toggle
                testId="notifications-storybook-toast-toggle-aggregate"
                label="Aggregate repeat clicks (counter)"
                checked={aggregate}
                onChange={setAggregate}
            />
            <Toggle testId="notifications-storybook-toast-toggle-persist" label="Persist (no auto-dismiss)" checked={persist} onChange={setPersist} />
            <Button data-testid="notifications-storybook-toast-show" variant="primary" onClick={handleShow} sx={{ mt: 4 }}>
                Show
            </Button>
        </Box>
    )
}

/**
 * Triggers the real `bannerUsecase`, rendered by the global `<BannerRenderer/>` - not a local preview.
 * `clear`s the fixed `dismissKey` before every `show`, or dismissing it once would silently no-op every later click.
 */
const BannerPlayground: FC = () => {
    const bannerUsecase = useAppUsecase('bannerUsecase')

    const [severity, setSeverity] = useState<BannerSeverity>(NotificationSeverity.INFO)
    const [showDescription, setShowDescription] = useState(true)
    const [showAction, setShowAction] = useState(false)

    const handleShow = (): void => {
        bannerUsecase.clear(BANNER_PLAYGROUND_DISMISS_KEY)
        bannerUsecase.show({
            severity,
            title: 'Banner title',
            dismissKey: BANNER_PLAYGROUND_DISMISS_KEY,
            ...(showDescription && { description: 'Full-width, session-scoped, dismissible until the condition it represents resolves.' }),
            ...(showAction && { action: { label: 'Resolve', onClick: () => {} } }),
        })
    }

    return (
        <Box sx={playgroundControlsStyle}>
            <SplitButtonControl
                ariaLabel="Banner severity"
                testIdPrefix="notifications-storybook-banner-severity"
                option={severity}
                options={bannerSeverities}
                onChange={setSeverity}
            />
            <Toggle
                testId="notifications-storybook-banner-toggle-description"
                label="Show description"
                checked={showDescription}
                onChange={setShowDescription}
            />
            <Toggle testId="notifications-storybook-banner-toggle-action" label="Show action" checked={showAction} onChange={setShowAction} />
            <Button data-testid="notifications-storybook-banner-show" variant="primary" onClick={handleShow} sx={{ mt: 4 }}>
                Show
            </Button>
        </Box>
    )
}

/** Renders directly, outside `bannerUsecase`, the way a page shows a banner it owns: no close button and not a live region, like the session banner on phones */
const FixedBannerPreview: FC = () => {
    const [severity, setSeverity] = useState<BannerSeverity>(NotificationSeverity.WARNING)

    return (
        <Box sx={playgroundStyle}>
            <Box sx={playgroundControlsStyle}>
                <SplitButtonControl
                    ariaLabel="Fixed banner severity"
                    testIdPrefix="notifications-storybook-fixed-banner-severity"
                    option={severity}
                    options={bannerSeverities}
                    onChange={setSeverity}
                />
            </Box>
            <Box sx={fixedBannerPreviewStyle}>
                <Banner data-testid="notifications-storybook-fixed-banner" severity={severity} live={false} title="Banner title, no close button" />
            </Box>
        </Box>
    )
}

/** Renders directly, no trigger - unlike Toast/Banner, InlineAlert is "not a system notification" per Figma and is meant to render as normal, static page content. */
const InlineAlertPlayground: FC = () => {
    const [severity, setSeverity] = useState<NotificationSeverity>(NotificationSeverity.INFO)
    const [contentKind, setContentKind] = useState<InlineAlertContentKind>('titleAndDescription')
    const [dismissible, setDismissible] = useState(false)

    return (
        <Box sx={playgroundStyle}>
            <Box sx={playgroundControlsStyle}>
                <SplitButtonControl
                    ariaLabel="Inline alert severity"
                    testIdPrefix="notifications-storybook-inline-alert-severity"
                    option={severity}
                    options={severities}
                    onChange={setSeverity}
                />
                <SplitButtonControl
                    ariaLabel="Inline alert content"
                    testIdPrefix="notifications-storybook-inline-alert-content"
                    option={contentKind}
                    options={inlineAlertContentKinds}
                    onChange={setContentKind}
                />
                <Toggle testId="notifications-storybook-inline-alert-toggle-dismissible" label="Dismissible" checked={dismissible} onChange={setDismissible} />
            </Box>
            <Box sx={previewStyle}>
                <InlineAlert severity={severity} content={inlineAlertContentByKind[contentKind]} onClose={dismissible ? () => {} : undefined} />
            </Box>
        </Box>
    )
}

export const Notifications: FC = () => (
    <>
        <Typography variant="subtitle1" sx={sectionTitleStyle}>
            Toast
        </Typography>
        <ToastPlayground />

        <Typography variant="subtitle1" sx={sectionTitleStyle}>
            Banner
        </Typography>
        <BannerPlayground />
        <FixedBannerPreview />

        <Typography variant="subtitle1" sx={sectionTitleStyle}>
            Inline Alert
        </Typography>
        <InlineAlertPlayground />
    </>
)
